import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import {
    FaPhone, FaEnvelope, FaMapMarkerAlt, FaUserNurse,
    FaTruck, FaEdit, FaSave, FaArrowLeft, FaTeethOpen,
    FaClipboardList, FaFileInvoiceDollar, FaClock, FaTrash, FaTools, FaFileInvoice,
    FaPlus, FaCheckCircle, FaExclamationTriangle, FaMoneyBillWave, FaCopy, FaCheck,
    FaExternalLinkAlt, FaWhatsapp, FaSearch, FaFilter, FaFileAlt, FaTooth, FaSmile,
    FaEye, FaDownload, FaTimes
} from 'react-icons/fa';
import InvoiceGenerator from '../components/InvoiceGenerator';
import QuoteGenerator from '../components/QuoteGenerator';
import WorkSheetGenerator from '../components/WorkSheetGenerator';
import PeriodicInvoiceGenerator from '../components/PeriodicInvoiceGenerator';
import AccountStatement from '../components/AccountStatement';
import { getDeadlineAlert } from '../utils/deadlineUtils';
import '../styles/ClientDetails.css';

const ClientDetails = () => {
    const { clientId } = useParams();
    const { clients, updateClient, orders, invoices, payments, creditNotes } = useData();
    const navigate = useNavigate();

    const [client, setClient] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({});
    const [activeTab, setActiveTab] = useState('travaux'); // 'travaux' | 'compta' | 'factures' | 'preferences'
    const [orderSearch, setOrderSearch] = useState('');
    const [orderStatusFilter, setOrderStatusFilter] = useState('all');
    const [invoiceSearch, setInvoiceSearch] = useState('');
    const [copiedId, setCopiedId] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    useEffect(() => {
        const foundClient = clients.find(c => c.id === clientId);
        if (foundClient) {
            setClient(foundClient);
            setFormData(foundClient);
        }
    }, [clientId, clients]);

    // Format initials for avatar
    const getInitials = (name) => {
        if (!name) return 'PR';
        const parts = name.replace(/^Dr\.?\s*/i, '').trim().split(/\s+/);
        if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    // WhatsApp clean phone URL
    const getWhatsAppUrl = (phone) => {
        if (!phone) return null;
        let clean = phone.replace(/[^0-9]/g, '');
        if (clean.startsWith('0')) {
            clean = '33' + clean.slice(1);
        }
        return `https://wa.me/${clean}`;
    };

    // Copy client ID
    const handleCopyId = () => {
        if (client?.id) {
            navigator.clipboard.writeText(client.id);
            setCopiedId(true);
            setTimeout(() => setCopiedId(false), 2000);
        }
    };

    // Client orders
    const clientOrders = useMemo(() => {
        if (!client) return [];
        return orders.filter(o => 
            o.clientId === clientId || 
            o.client_id === clientId || 
            o.practitionerName === client.name ||
            o.clientName === client.name
        ).sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    }, [orders, clientId, client]);

    // Client invoices
    const clientInvoices = useMemo(() => {
        if (!client) return [];
        return invoices.filter(i => {
            if (i.clientId === clientId || i.client_id === clientId) return true;
            if (i.orderId || i.order_id) {
                const linkedOrder = orders.find(o => o.id === (i.orderId || i.order_id));
                if (linkedOrder && (linkedOrder.clientId === clientId || linkedOrder.client_id === clientId)) return true;
            }
            const practName = i.practitionerName || i.practitioner_name;
            return practName === client.name || i.clientName === client.name;
        }).sort((a, b) => new Date(b.date || b.createdAt || 0) - new Date(a.date || a.createdAt || 0));
    }, [invoices, orders, clientId, client]);

    // Client payments
    const clientPayments = useMemo(() => {
        if (!client) return [];
        return payments.filter(p =>
            p.clientId === clientId ||
            p.client_id === clientId ||
            p.clientName === client.name
        );
    }, [payments, clientId, client]);

    // Client credit notes
    const clientCreditNotes = useMemo(() => {
        if (!client) return [];
        return creditNotes.filter(n =>
            n.clientId === clientId ||
            n.client_id === clientId ||
            n.clientName === client.name
        );
    }, [creditNotes, clientId, client]);

    // Financial calculations
    const totalBilled = useMemo(() => {
        return clientInvoices.reduce((acc, inv) => acc + (parseFloat(inv.amount || inv.total_amount || inv.total) || 0), 0);
    }, [clientInvoices]);

    const totalPaid = useMemo(() => {
        return clientPayments.reduce((acc, p) => {
            const amt = parseFloat(p.amount || 0);
            const isRefund = (p.payment_type || p.type || '').toLowerCase().includes('remboursement');
            return acc + (isRefund ? -amt : amt);
        }, 0);
    }, [clientPayments]);

    const totalCredits = useMemo(() => {
        return clientCreditNotes
            .filter(n => n.status !== 'pending')
            .reduce((acc, n) => acc + (parseFloat(n.amount) || 0), 0);
    }, [clientCreditNotes]);

    const balance = useMemo(() => {
        return totalBilled - totalPaid - totalCredits;
    }, [totalBilled, totalPaid, totalCredits]);

    // Case stats
    const totalCases = clientOrders.length;
    const activeCases = clientOrders.filter(o => {
        const s = (o.status || '').toLowerCase();
        return s !== 'livré' && s !== 'terminé' && s !== 'facturé';
    }).length;
    const urgentCases = clientOrders.filter(o => getDeadlineAlert(o)?.isUrgent).length;

    // Filtered orders for tab 1
    const filteredOrders = useMemo(() => {
        return clientOrders.filter(o => {
            const matchesSearch = !orderSearch ||
                (o.patientName || '').toLowerCase().includes(orderSearch.toLowerCase()) ||
                (o.caseNumber || '').toLowerCase().includes(orderSearch.toLowerCase()) ||
                (o.id || '').toLowerCase().includes(orderSearch.toLowerCase()) ||
                Object.values(o.teethConfig || {}).some(t => (t.restorationType || '').toLowerCase().includes(orderSearch.toLowerCase()));

            if (!matchesSearch) return false;

            if (orderStatusFilter === 'all') return true;
            const s = (o.status || '').toLowerCase();
            if (orderStatusFilter === 'en_cours') return s.includes('cours') || s.includes('fabrication') || s.includes('production');
            if (orderStatusFilter === 'en_attente') return s.includes('attente') || s.includes('nouveau') || s.includes('recu');
            if (orderStatusFilter === 'livre') return s.includes('livr') || s.includes('termin');
            if (orderStatusFilter === 'facture') return s.includes('factur');
            return true;
        });
    }, [clientOrders, orderSearch, orderStatusFilter]);

    // Filtered invoices for tab 3
    const filteredInvoices = useMemo(() => {
        return clientInvoices.filter(inv => {
            if (!invoiceSearch) return true;
            const search = invoiceSearch.toLowerCase();
            const invNum = (inv.invoiceNumber || inv.invoice_number || inv.id || '').toLowerCase();
            const patient = (inv.patientName || inv.patient_name || '').toLowerCase();
            return invNum.includes(search) || patient.includes(search);
        });
    }, [clientInvoices, invoiceSearch]);

    // Form handlers
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = () => {
        updateClient(clientId, formData);
        setClient(formData);
        setIsEditing(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
    };

    if (!client) {
        return (
            <div className="client-details-page container-fluid py-5 text-center">
                <div className="spinner-border text-primary mb-3" role="status"></div>
                <h5 className="text-muted">Chargement du dossier praticien...</h5>
            </div>
        );
    }

    const whatsappUrl = getWhatsAppUrl(client.phone);

    return (
        <div className="client-details-page container-fluid py-3">
            {/* Feedback alert on save */}
            {saveSuccess && (
                <div className="alert alert-success alert-dismissible fade show d-flex align-items-center shadow-sm mb-4" role="alert">
                    <FaCheckCircle className="me-2 fs-5" />
                    <strong>Modifications enregistrées !</strong> Le dossier du praticien a été mis à jour avec succès.
                </div>
            )}

            {/* Top Navigation & Practitioner Header Card */}
            <div className="client-hero-card card border-0 shadow-sm mb-4">
                <div className="card-body p-4">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                        {/* Left: Avatar + Identity */}
                        <div className="d-flex align-items-center gap-3">
                            <button
                                className="btn btn-light rounded-circle shadow-sm p-3 d-flex align-items-center justify-content-center"
                                onClick={() => navigate('/clients')}
                                title="Retour aux praticiens"
                                style={{ width: '44px', height: '44px' }}
                            >
                                <FaArrowLeft className="text-muted" />
                            </button>

                            <div className="position-relative">
                                <div className="client-avatar-circle">
                                    {getInitials(client.name)}
                                </div>
                                <span
                                    className={`client-status-indicator ${balance > 0 ? 'debt' : 'ok'}`}
                                    title={balance > 0 ? `Dette: ${balance.toFixed(2)} €` : 'Compte à jour'}
                                />
                            </div>

                            <div>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <h2 className="mb-0 fw-bold text-dark">{client.name}</h2>
                                    <span
                                        className="client-id-pill"
                                        onClick={handleCopyId}
                                        title="Cliquez pour copier l'identifiant"
                                    >
                                        ID: {client.id.substring(0, 8).toUpperCase()}
                                        {copiedId ? <FaCheck className="text-success ms-1" /> : <FaCopy className="ms-1 text-muted opacity-75" />}
                                    </span>
                                </div>
                                <div className="text-muted d-flex align-items-center gap-3 mt-1 flex-wrap small">
                                    {client.cabinetName && (
                                        <span className="fw-semibold text-secondary">
                                            🏢 {client.cabinetName}
                                        </span>
                                    )}
                                    {client.address && (
                                        <span>
                                            <FaMapMarkerAlt className="me-1 text-danger opacity-75" />
                                            {client.address}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Right: Quick Action Buttons */}
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            {whatsappUrl && (
                                <a
                                    href={whatsappUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-outline-success btn-sm d-flex align-items-center gap-1 shadow-xs"
                                    title="Envoyer un message WhatsApp"
                                >
                                    <FaWhatsapp className="fs-6" />
                                    <span>WhatsApp</span>
                                </a>
                            )}
                            {client.phone && (
                                <a
                                    href={`tel:${client.phone}`}
                                    className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                                    title="Appeler le cabinet"
                                >
                                    <FaPhone />
                                    <span>Appeler</span>
                                </a>
                            )}
                            {client.email && (
                                <a
                                    href={`mailto:${client.email}`}
                                    className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                                    title="Envoyer un email"
                                >
                                    <FaEnvelope />
                                    <span>Email</span>
                                </a>
                            )}
                            <button
                                className="btn btn-primary btn-sm d-flex align-items-center gap-1 shadow-xs"
                                onClick={() => navigate(`/jobs/new?clientId=${client.id}`)}
                                title="Créer un nouveau travail pour ce praticien"
                            >
                                <FaPlus />
                                <span>+ Nouveau Cas</span>
                            </button>
                            <PeriodicInvoiceGenerator
                                clientId={client.id}
                                clientName={client.name}
                                clientAddress={client.address}
                                clientPhone={client.phone}
                                clientEmail={client.email}
                                buttonLabel="Relevé / Facture"
                            />
                            {isEditing ? (
                                <button className="btn btn-success btn-sm px-3 fw-bold d-flex align-items-center gap-1 shadow-sm" onClick={handleSave}>
                                    <FaSave /> Enregistrer
                                </button>
                            ) : (
                                <button className="btn btn-outline-dark btn-sm px-3 d-flex align-items-center gap-1" onClick={() => { setIsEditing(true); setActiveTab('preferences'); }}>
                                    <FaEdit /> Modifier
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* 4 Financial & Operational KPI Cards */}
            <div className="row g-3 mb-4">
                {/* 1. Total Facturé */}
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="kpi-card kpi-card-blue shadow-sm">
                        <div className="d-flex justify-content-between align-items-start">
                            <div>
                                <span className="kpi-subtitle">Total Facturé</span>
                                <h3 className="kpi-amount">{totalBilled.toFixed(2)} €</h3>
                                <div className="kpi-meta">
                                    <span className="badge bg-white text-primary fw-semibold">
                                        {clientInvoices.length} factures générées
                                    </span>
                                </div>
                            </div>
                            <div className="kpi-icon-circle blue">
                                <FaFileInvoiceDollar />
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. Total Réglé (+ Avoirs) */}
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="kpi-card kpi-card-green shadow-sm">
                        <div className="d-flex justify-content-between align-items-start">
                            <div>
                                <span className="kpi-subtitle">Total Réglé (+ Avoirs)</span>
                                <h3 className="kpi-amount">{(totalPaid + totalCredits).toFixed(2)} €</h3>
                                <div className="kpi-meta">
                                    <span className="badge bg-white text-success fw-semibold">
                                        Règlements: {totalPaid.toFixed(2)} € {totalCredits > 0 ? `| Avoirs: ${totalCredits.toFixed(2)} €` : ''}
                                    </span>
                                </div>
                            </div>
                            <div className="kpi-icon-circle green">
                                <FaCheckCircle />
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3. Reste à Payer / Solde */}
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className={`kpi-card ${balance > 0 ? 'kpi-card-debt' : 'kpi-card-paid'} shadow-sm`}>
                        <div className="d-flex justify-content-between align-items-start">
                            <div>
                                <span className="kpi-subtitle">
                                    {balance > 0 ? 'Reste à Payer (Dette)' : balance < 0 ? 'Crédit Praticien' : 'Solde du Compte'}
                                </span>
                                <h3 className="kpi-amount">
                                    {balance > 0 ? `+ ${balance.toFixed(2)} €` : balance < 0 ? `- ${Math.abs(balance).toFixed(2)} €` : '0.00 €'}
                                </h3>
                                <div className="kpi-meta d-flex align-items-center gap-2">
                                    <span className={`badge ${balance > 0 ? 'bg-white text-danger' : 'bg-white text-success'} fw-semibold`}>
                                        {balance > 0 ? 'Paiement en attente' : balance < 0 ? 'Trop-perçu en crédit' : 'Compte soldé & à jour'}
                                    </span>
                                    {balance > 0 && (
                                        <button
                                            className="btn btn-sm btn-light text-danger fw-bold py-0 px-2"
                                            onClick={() => setActiveTab('compta')}
                                            style={{ fontSize: '0.75rem', borderRadius: '12px' }}
                                        >
                                            + Règlement
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className={`kpi-icon-circle ${balance > 0 ? 'red' : 'green'}`}>
                                {balance > 0 ? <FaExclamationTriangle /> : <FaSmile />}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 4. Travaux en cours */}
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="kpi-card kpi-card-purple shadow-sm">
                        <div className="d-flex justify-content-between align-items-start">
                            <div>
                                <span className="kpi-subtitle">Travaux en Atelier</span>
                                <h3 className="kpi-amount">{activeCases} <span className="fs-6 fw-normal text-white-50">/ {totalCases} cas</span></h3>
                                <div className="kpi-meta">
                                    {urgentCases > 0 ? (
                                        <span className="badge bg-danger text-white fw-bold animate__animated animate__pulse animate__infinite">
                                            ⚠️ {urgentCases} travail(aux) urgent(s)
                                        </span>
                                    ) : (
                                        <span className="badge bg-white text-purple fw-semibold">
                                            {totalCases - activeCases} livrés / terminés
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="kpi-icon-circle purple">
                                <FaClipboardList />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="client-tabs-container mb-4">
                <ul className="nav nav-pills client-tabs-nav shadow-sm p-1 rounded-pill bg-white">
                    <li className="nav-item">
                        <button
                            className={`nav-link rounded-pill ${activeTab === 'travaux' ? 'active' : ''}`}
                            onClick={() => setActiveTab('travaux')}
                        >
                            <FaClipboardList className="me-2" />
                            Travaux & Commandes
                            <span className="badge bg-secondary ms-2">{clientOrders.length}</span>
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link rounded-pill ${activeTab === 'compta' ? 'active' : ''}`}
                            onClick={() => setActiveTab('compta')}
                        >
                            <FaMoneyBillWave className="me-2" />
                            Comptabilité & Relevé
                            <span className={`badge ms-2 ${balance > 0 ? 'bg-danger' : 'bg-success'}`}>
                                {balance > 0 ? `${balance.toFixed(0)} €` : 'À jour'}
                            </span>
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link rounded-pill ${activeTab === 'factures' ? 'active' : ''}`}
                            onClick={() => setActiveTab('factures')}
                        >
                            <FaFileInvoice className="me-2" />
                            Factures
                            <span className="badge bg-secondary ms-2">{clientInvoices.length}</span>
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link rounded-pill ${activeTab === 'preferences' ? 'active' : ''}`}
                            onClick={() => setActiveTab('preferences')}
                        >
                            <FaTeethOpen className="me-2" />
                            Coordonnées & Préférences
                        </button>
                    </li>
                </ul>
            </div>

            {/* Tab 1: Travaux & Commandes */}
            {activeTab === 'travaux' && (
                <div className="card shadow-sm border-0 mb-4 animate__animated animate__fadeIn">
                    <div className="card-header bg-white border-bottom p-3">
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                            {/* Search bar */}
                            <div className="position-relative flex-grow-1" style={{ maxWidth: '380px' }}>
                                <FaSearch className="position-absolute text-muted" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                                <input
                                    type="text"
                                    className="form-control ps-5 rounded-pill shadow-none"
                                    placeholder="Rechercher un patient, cas, type..."
                                    value={orderSearch}
                                    onChange={(e) => setOrderSearch(e.target.value)}
                                />
                                {orderSearch && (
                                    <button className="btn btn-sm btn-link position-absolute text-muted" style={{ right: '8px', top: '50%', transform: 'translateY(-50%)' }} onClick={() => setOrderSearch('')}>
                                        <FaTimes />
                                    </button>
                                )}
                            </div>

                            {/* Status Filter Pills */}
                            <div className="d-flex gap-1 flex-wrap">
                                {[
                                    { key: 'all', label: 'Tous' },
                                    { key: 'en_cours', label: 'En cours' },
                                    { key: 'en_attente', label: 'En attente' },
                                    { key: 'livre', label: 'Livrés' },
                                    { key: 'facture', label: 'Facturés' }
                                ].map(filter => (
                                    <button
                                        key={filter.key}
                                        className={`btn btn-sm rounded-pill px-3 ${orderStatusFilter === filter.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => setOrderStatusFilter(filter.key)}
                                    >
                                        {filter.label}
                                    </button>
                                ))}
                            </div>

                            {/* Create New Case Button */}
                            <button
                                className="btn btn-success btn-sm rounded-pill px-3 shadow-xs d-flex align-items-center gap-1"
                                onClick={() => navigate(`/jobs/new?clientId=${client.id}`)}
                            >
                                <FaPlus /> Nouveau Travail
                            </button>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="ps-4">Date</th>
                                    <th>Patient</th>
                                    <th>Type de Travail</th>
                                    <th>Teinte</th>
                                    <th>Échéance</th>
                                    <th>Statut</th>
                                    <th>Facture</th>
                                    <th className="text-end pe-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredOrders.map(order => {
                                    const deadlineAlert = getDeadlineAlert(order);
                                    const linkedInvoice = clientInvoices.find(i => (i.orderId || i.order_id) === order.id || i.caseNumber === order.caseNumber);
                                    const teethList = Object.keys(order.teethConfig || {});
                                    const primaryRestoration = Object.values(order.teethConfig || {})[0]?.restorationType || order.workType || 'Prothèse';

                                    return (
                                        <tr
                                            key={order.id}
                                            onClick={() => navigate(`/jobs/${order.id}`)}
                                            style={{ cursor: 'pointer' }}
                                            className="align-middle"
                                        >
                                            <td className="ps-4 text-nowrap text-muted small">
                                                {order.createdAt ? new Date(order.createdAt).toLocaleDateString('fr-FR') : '-'}
                                            </td>

                                            <td>
                                                <div className="fw-bold text-dark">{order.patientName || 'Sans nom'}</div>
                                                <div className="text-muted font-monospace" style={{ fontSize: '0.75rem' }}>
                                                    {order.caseNumber || `#${order.id.substring(0, 8).toUpperCase()}`}
                                                </div>
                                            </td>

                                            <td>
                                                <div className="d-flex align-items-center gap-1 flex-wrap">
                                                    <span className="badge bg-light text-dark border">
                                                        {primaryRestoration}
                                                    </span>
                                                    {teethList.length > 0 && (
                                                        <span className="badge bg-secondary-subtle text-secondary" style={{ fontSize: '0.75rem' }}>
                                                            {teethList.map(t => `#${t}`).join(', ')}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            <td>
                                                <span className="badge bg-light text-secondary border">
                                                    {order.shade || Object.values(order.teethConfig || {})[0]?.details?.teinte || order.teinte || '-'}
                                                </span>
                                            </td>

                                            <td>
                                                {deadlineAlert ? (
                                                    <span className={deadlineAlert.badgeClass} title={deadlineAlert.label}>
                                                        <FaClock className="me-1" />
                                                        {deadlineAlert.shortLabel}
                                                    </span>
                                                ) : order.appointmentDate ? (
                                                    <span className="text-muted small">
                                                        {new Date(order.appointmentDate).toLocaleDateString('fr-FR')}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted small">-</span>
                                                )}
                                            </td>

                                            <td>
                                                <span className={`badge ${
                                                    ['terminé', 'livré', 'facturé'].includes((order.status || '').toLowerCase())
                                                        ? 'bg-success'
                                                        : (order.status || '').toLowerCase().includes('attente')
                                                        ? 'bg-warning text-dark'
                                                        : 'bg-primary'
                                                }`}>
                                                    {order.status || 'En cours'}
                                                </span>
                                            </td>

                                            <td>
                                                {linkedInvoice ? (
                                                    <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">
                                                        {linkedInvoice.invoiceNumber || 'Facturé'}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted small fst-italic">Non facturé</span>
                                                )}
                                            </td>

                                            <td className="text-end pe-4" onClick={(e) => e.stopPropagation()}>
                                                <div className="d-flex justify-content-end gap-2 align-items-center">
                                                    <WorkSheetGenerator order={order} buttonLabel="" />
                                                    <button
                                                        className="btn btn-sm btn-outline-primary shadow-xs"
                                                        onClick={() => navigate(`/jobs/edit/${order.id}`)}
                                                        title="Modifier la commande"
                                                    >
                                                        <FaEdit size={12} />
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-dark shadow-xs"
                                                        onClick={() => navigate(`/jobs/${order.id}`)}
                                                        title="Ouvrir le dossier complet"
                                                    >
                                                        <FaEye size={12} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}

                                {filteredOrders.length === 0 && (
                                    <tr>
                                        <td colSpan="8" className="text-center py-5 text-muted">
                                            <FaClipboardList className="fs-1 text-muted opacity-25 mb-2 d-block mx-auto" />
                                            <p className="mb-0">Aucun travail ne correspond à vos critères de recherche.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Tab 2: Comptabilité & Relevé */}
            {activeTab === 'compta' && (
                <div className="animate__animated animate__fadeIn mb-4">
                    <AccountStatement clientId={client.id} clientName={client.name} />
                </div>
            )}

            {/* Tab 3: Factures */}
            {activeTab === 'factures' && (
                <div className="card shadow-sm border-0 mb-4 animate__animated animate__fadeIn">
                    <div className="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="position-relative flex-grow-1" style={{ maxWidth: '350px' }}>
                            <FaSearch className="position-absolute text-muted" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                            <input
                                type="text"
                                className="form-control ps-5 rounded-pill shadow-none"
                                placeholder="Rechercher par N° ou Patient..."
                                value={invoiceSearch}
                                onChange={(e) => setInvoiceSearch(e.target.value)}
                            />
                        </div>
                        <PeriodicInvoiceGenerator
                            clientId={client.id}
                            clientName={client.name}
                            clientAddress={client.address}
                            clientPhone={client.phone}
                            clientEmail={client.email}
                            buttonLabel="Nouvelle Facture Périodique"
                        />
                    </div>

                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="ps-4">N° Facture</th>
                                    <th>Date</th>
                                    <th>Patient / Dossier</th>
                                    <th>Montant Total</th>
                                    <th>Statut</th>
                                    <th className="text-end pe-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredInvoices.map(inv => {
                                    const amount = parseFloat(inv.amount || inv.total_amount || inv.total || 0);
                                    return (
                                        <tr key={inv.id}>
                                            <td className="ps-4 fw-bold font-monospace text-primary">
                                                {inv.invoiceNumber || inv.invoice_number || inv.id?.substring(0, 8).toUpperCase()}
                                            </td>
                                            <td className="text-muted small">
                                                {inv.date ? new Date(inv.date).toLocaleDateString('fr-FR') : '-'}
                                            </td>
                                            <td>
                                                <div className="fw-semibold text-dark">{inv.patientName || inv.patient_name || 'Patient'}</div>
                                                {inv.caseNumber && <span className="text-muted small font-monospace">Cas: {inv.caseNumber}</span>}
                                            </td>
                                            <td className="fw-bold text-dark">
                                                {amount.toFixed(2)} €
                                            </td>
                                            <td>
                                                <span className={`badge ${inv.status === 'paid' || inv.status === 'Payée' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                                    {inv.status === 'paid' || inv.status === 'Payée' ? 'Payée' : 'Émise'}
                                                </span>
                                            </td>
                                            <td className="text-end pe-4">
                                                <InvoiceGenerator
                                                    invoice={inv}
                                                    isExisting={true}
                                                    buttonLabel="Télécharger / Voir PDF"
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}

                                {filteredInvoices.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5 text-muted">
                                            <FaFileInvoiceDollar className="fs-1 text-muted opacity-25 mb-2 d-block mx-auto" />
                                            <p className="mb-0">Aucune facture enregistrée pour ce praticien.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Tab 4: Coordonnées & Préférences Techniques */}
            {activeTab === 'preferences' && (
                <div className="row g-4 animate__animated animate__fadeIn mb-4">
                    {/* Coordonnées & Contact */}
                    <div className="col-lg-6">
                        <div className="card shadow-sm border-0 h-100">
                            <div className="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center">
                                <h5 className="mb-0 fw-bold d-flex align-items-center gap-2">
                                    <span className="icon-box blue"><FaUserNurse /></span> Coordonnées & Logistique
                                </h5>
                                {!isEditing && (
                                    <button className="btn btn-sm btn-outline-primary" onClick={() => setIsEditing(true)}>
                                        <FaEdit className="me-1" /> Modifier
                                    </button>
                                )}
                            </div>
                            <div className="card-body p-4">
                                <div className="mb-3">
                                    <label className="text-muted small text-uppercase fw-bold">Nom Praticien / Cabinet</label>
                                    {isEditing ? (
                                        <div className="row g-2">
                                            <div className="col-6">
                                                <input className="form-control" name="name" placeholder="Nom Praticien" value={formData.name || ''} onChange={handleChange} />
                                            </div>
                                            <div className="col-6">
                                                <input className="form-control" name="cabinetName" placeholder="Nom Cabinet" value={formData.cabinetName || ''} onChange={handleChange} />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="info-value fw-bold text-dark">
                                            {client.name} {client.cabinetName ? `(${client.cabinetName})` : ''}
                                        </div>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="text-muted small text-uppercase fw-bold">Adresse Cabinet</label>
                                    {isEditing ? (
                                        <textarea className="form-control" name="address" rows="2" value={formData.address || ''} onChange={handleChange} />
                                    ) : (
                                        <div className="info-value d-flex justify-content-between align-items-center">
                                            <div><FaMapMarkerAlt className="text-primary me-2" /> {client.address || 'Non renseignée'}</div>
                                            {client.address && (
                                                <a
                                                    href={`https://maps.google.com/?q=${encodeURIComponent(client.address)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm btn-light text-primary"
                                                    title="Ouvrir dans Google Maps"
                                                >
                                                    <FaExternalLinkAlt size={12} />
                                                </a>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="text-muted small text-uppercase fw-bold">Téléphone & Contact</label>
                                    {isEditing ? (
                                        <div className="row g-2">
                                            <div className="col-6">
                                                <input className="form-control" name="phone" placeholder="Téléphone" value={formData.phone || ''} onChange={handleChange} />
                                            </div>
                                            <div className="col-6">
                                                <input className="form-control" name="contactName" placeholder="Assistante / Contact" value={formData.contactName || ''} onChange={handleChange} />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="info-value">
                                            <div className="d-flex align-items-center gap-2">
                                                <FaPhone className="text-success" />
                                                <span>{client.phone || '-'}</span>
                                                {client.phone && (
                                                    <a href={`tel:${client.phone}`} className="badge bg-light text-success border ms-2 text-decoration-none">
                                                        Appeler
                                                    </a>
                                                )}
                                            </div>
                                            {client.contactName && (
                                                <div className="text-muted small mt-1 ps-4">Contact privilégié: {client.contactName}</div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="text-muted small text-uppercase fw-bold">Email</label>
                                    {isEditing ? (
                                        <input className="form-control" name="email" value={formData.email || ''} onChange={handleChange} />
                                    ) : (
                                        <div className="info-value">
                                            <FaEnvelope className="text-warning me-2" />
                                            <a href={`mailto:${client.email}`} className="text-dark text-decoration-none">
                                                {client.email || '-'}
                                            </a>
                                        </div>
                                    )}
                                </div>

                                <div className="row g-3">
                                    <div className="col-6">
                                        <label className="text-muted small text-uppercase fw-bold">Mode de Livraison</label>
                                        {isEditing ? (
                                            <select className="form-select" name="deliveryMode" value={formData.deliveryMode || 'FedEx'} onChange={handleChange}>
                                                <option value="FedEx">FedEx</option>
                                                <option value="CHRONO">CHRONO</option>
                                                <option value="Coursier">Coursier</option>
                                                <option value="Enlèvement">Enlèvement</option>
                                            </select>
                                        ) : (
                                            <div className="mt-1">
                                                <span className="badge-delivery">{client.deliveryMode || 'FedEx'}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-12">
                                        <label className="text-muted small text-uppercase fw-bold">Instructions d'Accès / Digicode</label>
                                        {isEditing ? (
                                            <textarea className="form-control" name="deliveryInstructions" rows="2" value={formData.deliveryInstructions || ''} onChange={handleChange} />
                                        ) : (
                                            <div className="info-value text-muted small fst-italic">
                                                {client.deliveryInstructions || "Pas d'instructions particulières."}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Préférences Techniques */}
                    <div className="col-lg-6">
                        <div className="card shadow-sm border-0 h-100">
                            <div className="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center">
                                <h5 className="mb-0 fw-bold d-flex align-items-center gap-2">
                                    <span className="icon-box orange"><FaTeethOpen /></span> Préférences Techniques Prothétiques
                                </h5>
                                {isEditing && (
                                    <button className="btn btn-sm btn-success fw-bold" onClick={handleSave}>
                                        <FaSave className="me-1" /> Enregistrer
                                    </button>
                                )}
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3 mb-4">
                                    <div className="col-6">
                                        <label className="text-muted small text-uppercase fw-bold">Point de Contact</label>
                                        {isEditing ? (
                                            <select className="form-select" name="contactPointPref" value={formData.contactPointPref || 'Normal'} onChange={handleChange}>
                                                <option value="Normal">Normal</option>
                                                <option value="Fort">Fort / Serré</option>
                                                <option value="Léger">Léger / Passif</option>
                                            </select>
                                        ) : (
                                            <div className="fw-bold text-dark fs-6 mt-1">
                                                <span className="badge bg-light text-dark border px-3 py-2">
                                                    {client.contactPointPref || 'Normal'}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="col-6">
                                        <label className="text-muted small text-uppercase fw-bold">Occlusion</label>
                                        {isEditing ? (
                                            <select className="form-select" name="occlusionPref" value={formData.occlusionPref || 'Normale'} onChange={handleChange}>
                                                <option value="Normale">Normale</option>
                                                <option value="Légère">Sous-occlusion légère (-0.1mm)</option>
                                                <option value="Forte">Forte / Contact franc</option>
                                            </select>
                                        ) : (
                                            <div className="fw-bold text-dark fs-6 mt-1">
                                                <span className="badge bg-light text-dark border px-3 py-2">
                                                    {client.occlusionPref || 'Normale'}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <label className="text-muted small text-uppercase fw-bold">Teinte habituelle / Défaut</label>
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="defaultShade"
                                            placeholder="Ex: A2, VITA Classical"
                                            value={formData.defaultShade || ''}
                                            onChange={handleChange}
                                        />
                                    ) : (
                                        <div className="info-value">
                                            {client.defaultShade ? (
                                                <span className="badge bg-warning-subtle text-warning-emphasis border">
                                                    {client.defaultShade}
                                                </span>
                                            ) : (
                                                <span className="text-muted small">Aucune teinte par défaut (selon prescription)</span>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="text-muted small text-uppercase fw-bold">Notes et exigences permanentes du laboratoire</label>
                                    {isEditing ? (
                                        <textarea
                                            className="form-control"
                                            rows="5"
                                            name="technicalNotes"
                                            placeholder="Spécifications particulières pour les techniciens (ex: profils d'émergence, épaisseur céramique, glaçure...)"
                                            value={formData.technicalNotes || ''}
                                            onChange={handleChange}
                                        />
                                    ) : (
                                        <div className="technical-note">
                                            {client.technicalNotes || "Aucune consigne technique particulière enregistrée pour ce praticien."}
                                        </div>
                                    )}
                                </div>

                                {isEditing && (
                                    <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                                        <button className="btn btn-light px-4" onClick={() => { setIsEditing(false); setFormData(client); }}>
                                            Annuler
                                        </button>
                                        <button className="btn btn-success px-4 fw-bold shadow-sm" onClick={handleSave}>
                                            <FaSave className="me-2" /> Enregistrer les Préférences
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientDetails;
